import { makeAutoObservable, runInAction } from 'mobx';
import { MatenRequestResponse } from '../../../shared';

export enum DialogType {
    TEXT = 'text',
    NUMBER = 'number',
    MULTI_FIELD = 'multi_field',
    DELETE = 'delete',
    CONFIRM = 'confirm',
    WAIT = 'wait',
}

interface DialogConfirmResponse {
    confirmed: boolean;
    error?: string;
    response?: object;
    values?: any;
}
interface DialogInputField {
    key: string;
    label: string;
    placeholder?: string;
    suffix?: string;
    type?: 'text' | 'password' | 'number' | 'textarea';
    required?: boolean;
    rows?: number; // For textarea fields
}

interface DialogInputState<T> {
    open: boolean;
    title: string;
    type: DialogType;
    message?: string;
    initialValue?: T;
    onConfirm?: (value: T) => Promise<MatenRequestResponse>;
    resolve: (result: { confirmed: boolean; error?: string; value?: T }) => void;
    fields?: DialogInputField[];
}

class DialogInputStore {
    public dialog: DialogInputState<any> | null = null;
    public dialogInputValues: Record<string, string | number | null> = {};
    public loading: boolean = false;
    private initialValues: Record<string, string | number | null> = {};
    private requiredFields: string[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    openDialog(
        type: 'text' | 'number',
        title: string,
        message?: string,
        label?: string,
        placeholder?: string,
        initialValue?: string | number | null,
        onConfirm?: (values: object) => Promise<MatenRequestResponse>
    ): Promise<DialogConfirmResponse> {
        return new Promise(resolve => {
            runInAction(() => {
                this.requiredFields = []; // Reset required fields
                this.dialog = {
                    open: true,
                    type: type === 'number' ? DialogType.NUMBER : DialogType.TEXT,
                    fields: [
                        {
                            key: 'input',
                            label: label || '',
                            placeholder: placeholder || 'Eingabe...',
                            type: type === 'number' ? 'number' : 'text',
                            required: true,
                        },
                    ],
                    title,
                    message,
                    onConfirm,
                    resolve,
                };
                this.dialogInputValues = { input: initialValue || null };
                this.initialValues = { input: initialValue || null };
                this.requiredFields.push('input'); // Add input as required field
            });
        });
    }

    openMultiFieldDialog(
        title: string,
        fields: DialogInputField[],
        message?: string,
        initialValues?: Record<string, string | number | null>,
        onConfirm?: (values: object) => Promise<MatenRequestResponse>
    ): Promise<DialogConfirmResponse> {
        return new Promise(resolve => {
            runInAction(() => {
                this.requiredFields = []; // Reset required fields
                this.dialog = {
                    open: true,
                    type: DialogType.MULTI_FIELD,
                    title,
                    message,
                    fields,
                    onConfirm,
                    resolve,
                };
                this.dialogInputValues = initialValues ? { ...initialValues } : {};
                // Initialize empty values for fields that don't have initial values
                fields.forEach(field => {
                    if (!(field.key in this.dialogInputValues)) {
                        this.dialogInputValues[field.key] = field.type === 'number' ? null : '';
                    }
                    if (field.required) {
                        this.requiredFields.push(field.key);
                    }
                });
                // Store initial values for comparison
                this.initialValues = { ...this.dialogInputValues };
            });
        });
    }

    openDeleteDialog(
        title: string,
        message: string,
        searchWord: string = '',
        onConfirm?: (accept: boolean) => Promise<MatenRequestResponse>
    ): Promise<DialogConfirmResponse> {
        return new Promise(resolve => {
            runInAction(() => {
                this.requiredFields = []; // Reset required fields
                this.dialog = {
                    open: true,
                    type: DialogType.DELETE,
                    title,
                    message,
                    onConfirm,
                    resolve,
                };
                this.dialogInputValues = {
                    confirmationText: '',
                    searchWord: searchWord || '',
                };
                this.initialValues = {};
            });
        });
    }

    openConfirmDialog(
        title: string,
        message: string,
        onConfirm?: () => Promise<MatenRequestResponse>
    ): Promise<DialogConfirmResponse> {
        return new Promise(resolve => {
            runInAction(() => {
                this.requiredFields = []; // Reset required fields
                this.dialog = {
                    open: true,
                    type: DialogType.CONFIRM,
                    title,
                    message,
                    onConfirm,
                    resolve,
                };
                this.dialogInputValues = {};
                this.initialValues = {};
            });
        });
    }

    openWaitDialog(
        title: string,
        message?: string,
        onConfirm?: () => Promise<MatenRequestResponse>
    ): Promise<DialogConfirmResponse> {
        return new Promise(resolve => {
            runInAction(() => {
                this.requiredFields = []; // Reset required fields
                this.dialog = {
                    open: true,
                    type: DialogType.WAIT,
                    title,
                    message,
                    onConfirm,
                    resolve,
                };
                this.dialogInputValues = {};
                this.initialValues = {};
            });
            const that = this;
            setTimeout(() => that.confirm(), 500);
        });
    }

    updateMultiFieldValue(key: string, value: string | number | null) {
        runInAction(() => {
            this.dialogInputValues[key] = value;
        });
    }

    public async confirm() {
        if (this.dialog) {
            let confirmResponse: DialogConfirmResponse = { confirmed: false };

            if (this.dialog.onConfirm) {
                runInAction(() => {
                    this.loading = true;
                });
                try {
                    const onConfirmResponse: MatenRequestResponse = await this.dialog.onConfirm(
                        this.isDeleteDialog || this.isWaitDialog
                            ? undefined
                            : this.dialogInputValues
                    );

                    // Process the response from onConfirm
                    confirmResponse = {
                        confirmed: onConfirmResponse.success,
                        response: onConfirmResponse.data,
                        error: onConfirmResponse.error,
                    };
                } catch (error: any) {
                    confirmResponse = {
                        confirmed: false,
                        error: error.message || 'ERR_UNKNOWN',
                    };
                } finally {
                    runInAction(() => {
                        this.loading = false;
                    });
                }
            } else {
                confirmResponse = {
                    confirmed: true,
                    values: this.dialogInputValues,
                };
            }

            this.dialog.resolve(confirmResponse);

            this.close();
        }
    }

    cancel() {
        if (this.dialog) {
            this.dialog.resolve({ confirmed: false });
            this.close();
        }
    }

    close() {
        this.dialog = null;
        this.dialogInputValues = {};
        this.initialValues = {};
        this.requiredFields = []; // Reset required fields
        this.loading = false;
    }

    get areRequiredFieldsValid(): boolean {
        if (!this.dialog || this.requiredFields.length === 0) return true;

        return this.requiredFields.every(fieldKey => {
            const value = this.dialogInputValues[fieldKey];
            // Check if value is not null, undefined, or empty string (trimmed)
            if (value === null || value === undefined) return false;
            if (typeof value === 'string' && value.trim() === '') return false;
            return true;
        });
    }

    get validationErrors(): string[] {
        if (!this.dialog) return [];

        const errors: string[] = [];

        this.requiredFields.forEach(fieldKey => {
            const value = this.dialogInputValues[fieldKey];
            const field = this.dialog?.fields?.find(f => f.key === fieldKey);
            const fieldLabel = field?.label || fieldKey;

            if (value === null || value === undefined) {
                errors.push(`${fieldLabel} ist erforderlich`);
            } else if (typeof value === 'string' && value.trim() === '') {
                errors.push(`${fieldLabel} darf nicht leer sein`);
            }
        });

        return errors;
    }

    get hasChanges(): boolean {
        if (!this.dialog) return false;

        // Compare current values with initial values
        const currentKeys = Object.keys(this.dialogInputValues);
        const initialKeys = Object.keys(this.initialValues);

        // Check if number of keys is different
        if (currentKeys.length !== initialKeys.length) return true;

        // Check if any value has changed
        return currentKeys.some(key => {
            const currentValue = this.dialogInputValues[key];
            const initialValue = this.initialValues[key];
            return currentValue !== initialValue;
        });
    }

    get dialogCloseable(): boolean {
        if (!this.dialog) return false;

        return !this.loading && !this.isDeleteDialog && !this.isWaitDialog;
    }

    get dialogHasFooter(): boolean {
        return this.dialog?.type !== DialogType.WAIT;
    }

    get buttonAcceptLabel(): string {
        switch (this.dialog?.type) {
            case DialogType.TEXT:
            case DialogType.NUMBER:
            case DialogType.MULTI_FIELD:
                return this.loading ? 'Speichern...' : 'Bestätigen';
            case DialogType.DELETE:
                return 'Löschen';
            case DialogType.CONFIRM:
                return 'Bestätigen';
            case DialogType.WAIT:
                return 'Abbrechen';
            default:
                return 'Ok';
        }
    }

    get buttonAcceptIcon(): string | undefined {
        switch (this.dialog?.type) {
            case DialogType.TEXT:
            case DialogType.NUMBER:
            case DialogType.MULTI_FIELD:
                return this.loading ? undefined : 'pi pi-check';
            case DialogType.DELETE:
                return 'pi pi-trash';
            case DialogType.CONFIRM:
                return 'pi pi-check';
            case DialogType.WAIT:
                return 'pi pi-spinner';
            default:
                return undefined;
        }
    }

    get buttonAcceptDisabled(): boolean {
        if (!this.dialog) return true;

        if (this.dialog.type === DialogType.DELETE) {
            // Disable button if confirmation text is not provided or does not match search word
            const confirmationText = (this.dialogInputValues['confirmationText'] as string) || '';
            const searchWord = (this.dialogInputValues['searchWord'] as string) || '';
            return confirmationText !== searchWord;
        }

        if (this.dialog.type === DialogType.CONFIRM || this.dialog.type === DialogType.WAIT) {
            return this.loading;
        }

        // For input dialogs, check required fields validation and changes
        return this.loading || (!this.hasChanges && !this.areRequiredFieldsValid);
    }

    get buttonCancelDisabled(): boolean {
        if (!this.dialog) return true;

        // Disable button if loading or no changes
        return this.loading;
    }

    get buttonAcceptTestId(): string {
        switch (this.dialog?.type) {
            case DialogType.TEXT:
            case DialogType.NUMBER:
            case DialogType.MULTI_FIELD:
                return 'dialog-input-confirm';
            case DialogType.DELETE:
                return 'delete-confirmation-dialog-confirm';
            case DialogType.CONFIRM:
                return 'dialog-confirmation-confirm';
            case DialogType.WAIT:
                return 'dialog-wait-confirm';
            default:
                return 'dialog-input-confirm';
        }
    }

    get buttonCancelTestId(): string {
        switch (this.dialog?.type) {
            case DialogType.TEXT:
            case DialogType.NUMBER:
            case DialogType.MULTI_FIELD:
                return 'dialog-input-cancel';
            case DialogType.DELETE:
                return 'delete-confirmation-dialog-cancel';
            case DialogType.CONFIRM:
                return 'dialog-confirmation-cancel';
            case DialogType.WAIT:
                return 'dialog-wait-cancel';
            default:
                return 'dialog-input-cancel';
        }
    }

    get buttonAcceptClassName(): string {
        return this.isDeleteDialog ? 'p-button-danger' : 'p-button-primary';
    }

    get isDeleteDialog(): boolean {
        return this.dialog?.type === DialogType.DELETE;
    }

    get isWaitDialog(): boolean {
        return this.dialog?.type === DialogType.WAIT;
    }
}

export const dialogInputStore = new DialogInputStore();
export type { DialogInputField };
